import type { inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from '@/types/appRouter';
import { Badge, Card } from '@/components/UI';

type RouterOutputs = inferRouterOutputs<AppRouter>;
type PrimaryObjectivePerformance = RouterOutputs['experiment']['results']['primaryObjective'];
type RealizedPerformanceSet = Extract<PrimaryObjectivePerformance, { mode: 'strategy_exit' }>['outcomeSets'][number];

interface PrimaryObjectivePerformanceCardProps {
    objective: PrimaryObjectivePerformance;
    automaticParameterDiscoveryEnabled: boolean;
}

function formatPercent(value: number | null): string {
    return value === null ? '—' : `${value.toFixed(2)}%`;
}

function percentTone(value: number | null): string {
    if (value === null) return 'text-zinc-400';
    return value >= 0 ? 'text-green-400' : 'text-red-400';
}

function statusVariant(
    status: RealizedPerformanceSet['status'] | Extract<PrimaryObjectivePerformance, { mode: 'strategy_exit' }>['objectiveStatus']
) {
    switch (status) {
        case 'ready':
            return 'success' as const;
        case 'failed':
        case 'cancelled':
        case 'stale':
            return 'danger' as const;
        case 'preparing':
        case 'materializing':
        case 'queued':
            return 'warning' as const;
        default:
            return 'secondary' as const;
    }
}

function Metric({ label, value, tone = 'text-white' }: { label: string; value: string; tone?: string }) {
    return (
        <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-4">
            <dt className="text-xs font-medium uppercase tracking-wide text-zinc-500">{label}</dt>
            <dd className={`mt-1 text-xl font-semibold ${tone}`}>{value}</dd>
        </div>
    );
}

function ReadyOutcomeSet({ outcomeSet, usesOptionHeadline }: { outcomeSet: RealizedPerformanceSet; usesOptionHeadline: boolean }) {
    const headlineTradeCount = usesOptionHeadline ? outcomeSet.optionTradeCount : outcomeSet.profitTradeCount;
    const average = usesOptionHeadline ? outcomeSet.avgOptionProfitPercent : outcomeSet.avgProfitPercent;
    const median = usesOptionHeadline ? outcomeSet.medianOptionProfitPercent : outcomeSet.medianProfitPercent;
    const winRate = usesOptionHeadline ? outcomeSet.optionWinRate : outcomeSet.winRate;
    const coverage = outcomeSet.entryCount > 0 ? (headlineTradeCount / outcomeSet.entryCount) * 100 : null;

    return (
        <section
            className="rounded-lg border border-zinc-800 bg-zinc-900/60 p-4"
            aria-labelledby={`outcome-set-${outcomeSet.outcomeSetId}`}
        >
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h3 className="font-semibold text-white" id={`outcome-set-${outcomeSet.outcomeSetId}`}>
                        {outcomeSet.duration} · {outcomeSet.entityType ?? 'All entities'}
                    </h3>
                    <p className="mt-1 text-xs text-zinc-500">Realized outcome set #{outcomeSet.outcomeSetId}</p>
                </div>
                <Badge variant={statusVariant(outcomeSet.status)}>{outcomeSet.status}</Badge>
            </div>

            <dl className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
                <Metric
                    label={usesOptionHeadline ? 'Avg realized option return' : 'Avg realized return'}
                    tone={percentTone(average)}
                    value={formatPercent(average)}
                />
                <Metric label="Median" tone={percentTone(median)} value={formatPercent(median)} />
                <Metric label="Win rate" value={formatPercent(winRate)} />
                <Metric
                    label={usesOptionHeadline ? 'Option trades' : 'Executed trades'}
                    value={`${headlineTradeCount}/${outcomeSet.entryCount}`}
                />
                <Metric label="Coverage" value={formatPercent(coverage)} />
            </dl>

            <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-xs text-zinc-400">
                <span>Executed exits: {outcomeSet.executedTradeCount}</span>
                <span>Incomplete: {outcomeSet.incompleteCount}</span>
                <span>No option: {outcomeSet.skippedNoOptionCount}</span>
                <span>No trade: {outcomeSet.skippedNoTradeCount}</span>
                <span className={outcomeSet.runtimeErrorCount > 0 ? 'text-red-400' : undefined}>
                    Runtime errors: {outcomeSet.runtimeErrorCount}
                </span>
            </div>
        </section>
    );
}

export default function PrimaryObjectivePerformanceCard({
    objective,
    automaticParameterDiscoveryEnabled
}: PrimaryObjectivePerformanceCardProps) {
    if (objective.mode === 'fixed_horizon') return null;

    const optionStrategyLabel =
        objective.optionBuyingStrategyIds.length > 0 ? objective.optionBuyingStrategyIds.map((id) => `#${id}`).join(', ') : 'None';
    const usesOptionHeadline = objective.optionBuyingStrategyIds.length > 0;

    return (
        <Card className="border-cyan-800 bg-zinc-900" bodyClassName="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <div className="flex flex-wrap items-center gap-2">
                        <h2 className="text-xl font-semibold text-white">Primary scoring objective</h2>
                        <Badge variant="info">Realized global exit</Badge>
                        <Badge variant={statusVariant(objective.objectiveStatus)}>{objective.objectiveStatus}</Badge>
                    </div>
                    <p className="mt-2 text-sm text-zinc-300">
                        Global exit candidate #{objective.exitCandidateId} · Option strategy {optionStrategyLabel}
                    </p>
                    <p className="mt-1 text-sm text-zinc-500">
                        These results replay the selected exit and are the experiment&apos;s headline performance.
                    </p>
                </div>
            </div>

            {objective.supersededStockExecutionPolicyNotice && (
                <div className="rounded-lg border border-yellow-700/70 bg-yellow-950/30 p-4 text-sm text-yellow-200" role="status">
                    {objective.supersededStockExecutionPolicyNotice}
                </div>
            )}

            {objective.outcomeSets.length === 0 ? (
                <div className="rounded-lg border border-yellow-700/70 bg-yellow-950/30 p-4 text-sm text-yellow-200" role="status">
                    {objective.objectiveStatus === 'draft' && !automaticParameterDiscoveryEnabled
                        ? 'Realized performance has not been materialized because automatic parameter discovery is disabled. It will be prepared when requested from Parameter Discovery or by a Screener Optimizer job.'
                        : `Realized performance is ${
                              objective.objectiveStatus === 'failed'
                                  ? 'unavailable because preparation failed'
                                  : objective.objectiveStatus === 'ready'
                                    ? 'unavailable because no matching outcome set was found'
                                    : 'not ready yet'
                          }.`}{' '}
                    Fixed-horizon results below are not substituted for the selected global-exit objective.
                </div>
            ) : (
                objective.outcomeSets.map((outcomeSet) =>
                    outcomeSet.status === 'ready' ? (
                        <ReadyOutcomeSet key={outcomeSet.outcomeSetId} outcomeSet={outcomeSet} usesOptionHeadline={usesOptionHeadline} />
                    ) : (
                        <section className="rounded-lg border border-yellow-700/70 bg-yellow-950/30 p-4" key={outcomeSet.outcomeSetId}>
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <div>
                                    <h3 className="font-semibold text-yellow-100">
                                        {outcomeSet.duration} · {outcomeSet.entityType ?? 'All entities'}
                                    </h3>
                                    <p className="mt-1 text-sm text-yellow-200">
                                        Realized performance is {outcomeSet.status}; no proxy metric is shown in its place.
                                    </p>
                                </div>
                                <Badge variant={statusVariant(outcomeSet.status)}>{outcomeSet.status}</Badge>
                            </div>
                        </section>
                    )
                )
            )}
        </Card>
    );
}
